import type { FiveStepConfig } from "@/content/types";

type Props = { config: FiveStepConfig };

/* Reusable five-step flow SVG — parametrised per case.
   Fixed geometry (1060×620 viewBox) matches the mockup verbatim.
   CSS lives in dossier.css under `.ec2` scope (global, not a module). */
export function FiveStepDiagram({ config }: Props) {
  const { steps, gapDays, selfLoops, topNotes, cancelStub, declaredMedianDays, legend } = config;

  const boxX: readonly [number, number, number, number, number] = [60, 240, 420, 600, 780];
  const boxW = 140;
  const boxY = 294;
  const boxH = 100;

  const xAt = (i: number): number => boxX[i]!;

  const loopArcs = (selfLoops ?? []).map((sl) => {
    const xL = xAt(sl.fromStep - 1) + 40;
    const xR = xAt(sl.fromStep - 1) + 100;
    return {
      d: `M ${xL} ${boxY} C ${xL} ${boxY - 80}, ${xR} ${boxY - 80}, ${xR} ${boxY}`,
      lblX: (xL + xR) / 2,
      lblY: boxY - 56,
      label: sl.label,
    };
  });

  const cancelStep = (cancelStub?.fromStep ?? 4) - 1;
  const cancelX = xAt(cancelStep) + boxW / 2;

  const connectorColor = (c?: "teal" | "signal" | "alert" | "muted") =>
    c === "teal" ? "tick-teal" : c === "signal" ? "tick-signal" : c === "alert" ? "tick-alert" : "tick-muted";

  return (
    <div className="diagram-wrap-inner">
      <div className="diagram">
        <div className="hdrrow">
          <div className="ex">{config.exhibitTag}</div>
          <div className="ex">{config.sourceTag}</div>
        </div>

        <div className="ec2">
          <svg viewBox="0 0 1060 620" preserveAspectRatio="xMidYMid meet">
            {/* Self-loop arcs */}
            {loopArcs.map((a, i) => (
              <g key={`loop-${i}`}>
                <path className="arc-loop" d={a.d} />
                <text className="arc-lbl" x={a.lblX} y={a.lblY} textAnchor="middle">{a.label}</text>
              </g>
            ))}

            {/* Top notes */}
            {(topNotes ?? []).map((n, i) => {
              const x = n.x ?? 260;
              const y = n.y ?? 70;
              const w = n.width ?? 220;
              const fromX = xAt(n.fromStep - 1) + boxW / 2;
              const fromY = boxY - 6;
              const startX = x + w / 2;
              const startY = y + 62;
              return (
                <g key={`note-${i}`}>
                  <rect className="note" x={x} y={y} width={w} height={62} rx={2} />
                  <text className="note-txt" x={x + 14} y={y + 26}>{n.text}</text>
                  <path className="connector" d={`M ${startX} ${startY} L ${startX} ${fromY - 4} L ${fromX} ${fromY - 4} L ${fromX} ${fromY}`} />
                  <circle className={connectorColor(n.tickColor)} cx={fromX} cy={fromY} r={3} />
                </g>
              );
            })}

            {/* Five step boxes + bands */}
            {steps.map((s, i) => {
              const x = xAt(i);
              return (
                <g key={`step-${i}`}>
                  <rect className="box" x={x} y={boxY} width={boxW} height={boxH} />
                  <rect className={`band-${s.band}`} x={x} y={boxY} width={boxW} height={7} />
                  <text className={`step-num step-num-${s.band}`} x={x + 14} y={boxY + 26}>{s.num}</text>
                  <text className="step-name" x={x + 14} y={boxY + 50}>{s.name}</text>
                  {s.subActs[0] && (
                    <text className="sub-act" x={x + 14} y={boxY + 72}>{s.subActs[0]}</text>
                  )}
                  {s.subActs[1] && (
                    <text className="sub-act" x={x + 14} y={boxY + 86}>{s.subActs[1]}</text>
                  )}
                </g>
              );
            })}

            {/* Gap flows */}
            {gapDays.map((g, i) => {
              const xFrom = xAt(i) + boxW;
              const xTo = xAt(i + 1);
              return (
                <g key={`gap-${i}`}>
                  <path className="flow" d={`M ${xFrom} ${boxY + 50} L ${xTo} ${boxY + 50}`} />
                  <text className="gap-num" x={(xFrom + xTo) / 2} y={boxY + 36}>{g}</text>
                </g>
              );
            })}

            {/* Cancel stub */}
            {cancelStub && (
              <g>
                <path className="connector-cancel" d={`M ${cancelX} ${boxY + boxH} L ${cancelX} 500`} />
                <rect className="cancel-stub" x={cancelX - 70} y={500} width={140} height={30} rx={2} />
                <text className="cancel-stub-txt" x={cancelX} y={520}>{cancelStub.label}</text>
                {cancelStub.note && (
                  <>
                    <rect className="note" x={cancelX - 160} y={552} width={320} height={46} rx={2} />
                    <text className="note-txt" x={cancelX - 146} y={580}>{cancelStub.note}</text>
                    <path className="connector-cancel" d={`M ${cancelX - 90} 552 L ${cancelX - 90} 530`} />
                  </>
                )}
              </g>
            )}

            {/* Declared median callout */}
            {declaredMedianDays && (
              <g>
                <rect className="note" x={60} y={448} width={280} height={46} rx={2} />
                <text className="note-txt" x={74} y={476}>
                  declared median · apply → decide:{" "}
                  <tspan className="n"> {declaredMedianDays}</tspan>
                </text>
                <path className="connector" d={`M 200 448 L 200 ${boxY + boxH + 1}`} />
                <circle className="tick-teal" cx={200} cy={boxY + boxH + 1} r={3} />
              </g>
            )}
          </svg>
        </div>

        <div className="ec2-legend">
          {legend.map((l, i) => (
            <span key={i}>
              <span className="sw" style={{ background: bandColor(l.band) }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function bandColor(b: "teal" | "muted" | "signal" | "alert" | "ink"): string {
  switch (b) {
    case "teal":   return "#1E6B5A";
    case "muted":  return "#6B6B64";
    case "signal": return "#D48C1E";
    case "alert":  return "#A83625";
    case "ink":    return "#1A1C20";
  }
}
